import { AlertTriangle } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const procenaToksicneVeze: QuizConfig = {
  id: "procena-toksicne-veze",
  illustration: {
    src: "/images/quizzes/procena-toksicne-veze.webp",
    alt: "Partneri sede udaljeni na sofi posle neprijatnog razgovora.",
  },
  lastModified: "2026-10-05",
  title: "Koliko je vaša veza toksična?",
  shortTitle: "Test za toksicnu vezu",
  description:
    "Otkrijte da li u vašoj vezi postoje toksični obrasci i kako oni utiču na vas.",
  longDescription:
    "Analiziraj da li u vezi postoje obrasci kontrole, emocionalne manipulacije ili stalnog omalovažavanja koji ti crpe energiju. Test kroz konkretne scenarije hvata trenutke kada se osećaš nesigurno, kada se partner ponaša nepredvidivo i kada upadaš u začarani krug krivice. Na kraju dobijaš jasnu procenu nivoa toksičnosti, savete kako da postaviš granice i signale da li je vreme da potražiš podršku ili napraviš ozbiljne promene.",
  icon: AlertTriangle,
  duration: "6-8 min",
  category: "Odnosi i komunikacija",
  relatedDirectorySlugs: ["odnosi", "emocionalno-zlostavljanje"],
  focusAreasWanted: [
    "narcissistic_abuse",
    "boundaries",
    "couples_issues",
    "relationships",
  ],
  safety: {
    urgentOptionTexts: [
      "Partner koristi novac kao sredstvo kontrole i manipulacije",
      "Zastrašujuća i veoma bolna",
    ],
  },
  questions: [
    {
      type: "radio",
      question: "Kako se osećaš većinu vremena u vezi?",
      options: [
        "Sigurno, voljeno i poštovano",
        "Ponekad zabrinuto, ali uglavnom srećno",
        "Često anksiozno i nesigurno",
        "Kao da stalno hodam po ivici, u strahu od partnerove reakcije",
        "Emocionalno iscrpljeno i nesrećno",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako vaš partner reaguje kada izraziš neslaganje?",
      options: [
        "Sluša i pokušava da razume moje mišljenje",
        "Ponekad postane defanzivan, ali smireno pričamo",
        "Često me kritikuje i omalovažava moje mišljenje",
        "Nekada viče i koristi uvrede",
        "Ignoriše me ili kažnjava ćutanjem",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Šta se najčešće dešava kada imate sukob? (označi sve što važi)",
      options: [
        "Rešimo problem kroz zdrav razgovor",
        "Jedno od nas preti raskidom",
        "Partner prebacuje krivicu na mene, bez obzira na situaciju",
        "Nakon svađe, osećam se loše i nesigurno",
        "Svađe eskaliraju do vikanja i uvreda",
      ],
      scores: [0, 2, 3, 3, 4],
    },
    {
      type: "radio",
      question: "Kako tvoj partner reaguje na tvoje uspehe?",
      options: [
        "Podržava me i raduje se sa mnom",
        "Ponekad deluje nezainteresovano",
        "Umanjuje moj uspeh ili ga ignoriše",
        "Pokušava da se takmiči sa mnom",
        "Omaložava me i osećam se loše zbog svog uspeha",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Kako se osećaš kada treba da razgovaraš sa partnerom o svojim emocijama?",
      options: [
        "Slobodno i sigurno",
        "Ponekad se plašim kako će reagovati",
        "Često osećam da moje emocije nisu važne",
        "Većinom krijem svoja osećanja da ne bih izazvao/la konflikt",
        "Nikada ne osećam da mogu iskreno da pričam",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Kako tvoj partner pokazuje ljubav prema tebi? (označi sve što važi)",
      options: [
        "Podrškom, poštovanjem i nežnošću",
        "Ponekad mi pokazuje ljubav, ali nekad je hladan/na",
        "Ljubav pokazuje samo kada nešto želi zauzvrat",
        "Često koristi ljubav kao način kontrole",
        "Retko pokazuje ljubav i osećam se nevoljeno",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Kako partner reaguje kada provodiš vreme sa prijateljima ili porodicom?",
      options: [
        "Podržava me i razume",
        "Ponekad pokazuje ljubomoru, ali ništa ozbiljno",
        "Često me tera da biram između njega/nje i drugih",
        "Osećam se krivo kada provodim vreme sa drugima",
        "Ne dopušta mi da se viđam sa nekim bez problema",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često partner kritikuje tvoj izgled, ponašanje ili odluke?",
      options: [
        "Nikada, podržava me kakav/kakva jesam",
        "Ponekad daje konstruktivne savete",
        "Često mi govori kako bih trebao/la da se promenim",
        "Redovno mi govori da nisam dovoljno dobar/ra",
        "Koristi uvrede i omalovažavanje kako bih se osećao/la loše",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Kako se tvoj partner ponaša kada se ne slažete oko nečega? (označi sve što važi)",
      options: [
        "Sasluša me i pokušavamo da pronađemo rešenje",
        "Ignoriše me dok ne popustim",
        "Ljubomoran/na je i često me optužuje bez razloga",
        "Koristi manipulaciju da dobije šta želi",
        "Postaje agresivan/na ili me emocionalno kažnjava",
      ],
      scores: [0, 2, 3, 3, 4],
    },
    {
      type: "radio",
      question: "Kako bi opisao/la svoju vezu u ovom trenutku?",
      options: [
        "Srećna i ispunjena",
        "Ponekad izazovna, ali uglavnom dobra",
        "Često stresna i puna nesporazuma",
        "Emocionalno iscrpljujuća",
        "Zastrašujuća i veoma bolna",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se osećaš u vezi svojih finansija u vezi?",
      options: [
        "Imamo zdrav odnos prema novcu i zajedničkim odlukama",
        "Ponekad imamo neslaganja, ali ih rešavamo",
        "Partner često kontroliše moje finansije",
        "Moram da pravdam svaki trošak i tražim dozvolu",
        "Partner koristi novac kao sredstvo kontrole i manipulacije",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Kako se osećaš nakon druženja sa partnerom? (označi sve što važi)",
      options: [
        "Ispunjeno i srećno",
        "Ponekad iscrpljeno, ali uglavnom dobro",
        "Često zbunjeno i nesigurno u sebe",
        "Kao da moram da se oporavim emocionalno",
        "Anksiozno i sa osećajem da hodam po jajima",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često osećaš da moraš da se izvinjavaš za stvari koje nisu tvoja krivica?",
      options: [
        "Nikada, izvinjavam se samo kada sam zaista pogrešio/la",
        "Retko, uglavnom imamo zdrav odnos prema greškama",
        "Povremeno, da bih izbegao/la sukob",
        "Često, to je lakše nego da se suprotstavim",
        "Stalno se izvinjavam za sve, čak i kada nisam kriv/a",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Kako se osećaš kada razmišljaš o budućnosti sa svojim partnerom?",
      options: [
        "Uzbuđeno i sa nadom",
        "Uglavnom pozitivno, sa malo nesigurnosti",
        "Zabrinuto i nesigurno",
        "Anksiozno i uplašeno",
        "Zarobljeno i bez nade",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question: "Opiši šta te najviše brine u vezi sa tvojom vezom:",
    },
  ],
  copy: {
    title: "Nivo toksičnosti vaše veze",
    subtitle: "Razumevanje dinamike vaše veze",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
